//src/components/StockList.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchProducts4manual, createNewProductItem } from '../utils/api';
import { displayPrice, Product, StockItem, PaginatedStockResponse } from '../utils/types';
import { toast } from 'react-toastify';
import Pagination from './Pagination';
import { useStockLocationContext } from '../context/StockLocationContext';


const PRODUCTS_PER_PAGE_MODAL = 10;

const StockList: React.FC = () => {

    const { t } = useTranslation();
    const { locations } = useStockLocationContext();

    const [selectedItems, setSelectedItems] = useState<Product[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [stocks, setStocks] = useState<Product[]>([]);
    const [loadingStocks, setLoadingStocks] = useState(true);
    const [errorStocks, setErrorStocks] = useState<string | null>(null);

    const [selectedLocationId, setSelectedLocationId] = useState<number | 'all'>('all');
    const [isAutocompleteVisible, setIsAutocompleteVisible] = useState(false);
    const searchContainerRef = useRef<HTMLDivElement>(null);

    const [filterMode, setFilterMode] = useState<'all' | 'inStockOnly'>('all');

    // Filtered stocks
    // const filteredStocks = filterMode === 'inStockOnly'
    //     ? stocks.filter(stock => stock.total_quantity > 0)
    //     : stocks;
    const filteredStocks = stocks.filter(stock => {
        // Apply in-stock filter
        if (filterMode === 'inStockOnly' && stock.total_quantity <= 0) {
            return false;
        }

        // Apply location filter (if not "all")
        if (selectedLocationId !== 'all') {
            return (
                stock.stock_by_location &&
                stock.stock_by_location[selectedLocationId] &&
                stock.stock_by_location[selectedLocationId] > 0
            );
        }

        // console.log("Stock passes filters:", stock);

        return true;
    });


    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    const loadProducts = useCallback(async () => {
        setLoadingStocks(true);
        setErrorStocks(null);

        try {
            const { products: fetchedProducts, total_pages } = await fetchProducts4manual(
                currentPage,
                searchTerm,
                null,
                null,
                PRODUCTS_PER_PAGE_MODAL,
                'updated_at DESC',
                filterMode === 'inStockOnly' ? true : undefined
            );

            // console.log('stocklist fetchedProducts >>', fetchedProducts);

            setStocks(fetchedProducts);
            setTotalPages(total_pages);
        } catch (err: any) {
            setErrorStocks(err.message || 'Failed to load stocks.');
        } finally {
            setLoadingStocks(false);
        }
    }, [currentPage, searchTerm, filterMode]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    // --- Click Outside Handlers for Autocomplete ---
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                setIsAutocompleteVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (errorStocks) return <div className="text-red-600">{errorStocks}</div>;



    const handleCreateNewItem = async () => {
        // onClose();
        // You can open a modal, or call a callback, or navigate to a product creation page
        // toast.info(`Create new product: "${searchTerm}"`);

        if (!searchTerm.trim()) {
            toast.error(t('productSelector.productNameEmpty') as string);
            return;
        }

        // TODO: Implement a way to get category_id and product_value (SRP) from the user.
        // For now, using placeholder/default values.
        const defaultCategoryId = 1; // Placeholder: e.g., 'Uncategorized' or first available
        const defaultProductValue = 0.00; // Placeholder: SRP

        toast.warn(
            t('productSelector.usingDefaultsForNewProduct', { categoryId: defaultCategoryId, srp: displayPrice(defaultProductValue) }) as string,
            { autoClose: 7000 }
        );

        const payload = {
            product_name: searchTerm,
            product_cat: defaultCategoryId,
            product_value: defaultProductValue,
            // product_code can be omitted if backend generates it
        };

        try {
            const response = await createNewProductItem(payload);

            toast.success(t('productSelector.productCreated', { name: response.product.product_name }) as string);

            // onProductSelect(response.product, 1, response.product.product_value); // Add to order

        } catch (error: any) {
            console.error("Failed to create product:", error);
            toast.error(t('productSelector.createFailed', { error: error.response?.data?.message || error.message || t('errors.unknownError') }) as string);
        }

    };


    const getLocationName = (locationId: number | string) => {
        const id = Number(locationId);
        const location = locations.find((loc) => loc.id === id);
        return location ? location.name : 'ID#' + id;
    };


    return (
        <div className="mt-0 w-full md:w-full top-15 right-2 bg-white">
            <div className='w-72 md:w-5/6 fixed top-12 pt=4 left-24 md:left-40 z-10 bg-white p-4 rounded-lg shadow-lg'>
                <h2 className="text-xl font-semibold mb-4 text-center">Stock Item List</h2>

                <div className="flex flex-col md:flex-row justify-center items-start md:items-center gap-4 mb-1">

                    <div className="relative w-full md:w-1/3" ref={searchContainerRef}>
                        <div className="flex items-center">
                            <input
                                type="text"
                                placeholder={t('productSelector.searchPlaceholder') as string}
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                    setIsAutocompleteVisible(true);
                                }}
                                onFocus={() => setIsAutocompleteVisible(true)}
                                className="w-full p-2 border border-gray-300 rounded"
                                onKeyDown={e => {
                                    // if (searchTerm && e.key === 'Enter') {
                                    //     e.preventDefault();
                                    //     handleCreateNewItem();
                                    // }
                                }}
                                autoComplete="off"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => { setSearchTerm(''); setIsAutocompleteVisible(false); }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                                >
                                    &times;
                                </button>
                            )}
                        </div>

                        {isAutocompleteVisible && searchTerm && stocks.length > 0 && (
                            <ul className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-60 overflow-y-auto">
                                {stocks.map(product => (
                                    <li
                                        key={product.id}
                                        onClick={() => {
                                            setSearchTerm(product.product_name);
                                            setIsAutocompleteVisible(false);
                                            console.log("Selected item.product:", product);
                                        }}
                                        className="p-2 hover:bg-blue-100 cursor-pointer text-sm"
                                    >
                                        {product.product_name} <span className="text-xs text-gray-500">({product.product_code})</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {searchTerm && !loadingStocks && stocks.length === 0 && isAutocompleteVisible && (
                            <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-20 p-2 text-sm text-gray-500">
                                No products found.
                            </div>
                        )}

                        {/* {searchTerm && ( // Show "New" button next to search bar
                            <button
                                type="button"
                                onClick={handleCreateNewItem}
                                className="ml-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-semibold"
                            >
                                + {t('buttons.new')}
                            </button>
                        )} */}

                    </div>

                    <div className="flex flex-row justify-center gap-4 mb-1">
                        <select
                            value={selectedLocationId}
                            onChange={(e) =>
                                setSelectedLocationId(e.target.value === 'all' ? 'all' : Number(e.target.value))
                            }
                            className="border border-gray-300 rounded p-2 text-sm"
                        >
                            <option value="all">All Locations</option>
                            {locations.map(loc => (
                                <option key={loc.id} value={loc.id}>
                                    {loc.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-row justify-center gap-4 mb-1">
                        <button
                            onClick={() => {
                                if (filterMode === 'all') {
                                    loadProducts();
                                } else {
                                    setFilterMode('all');
                                    setCurrentPage(1);
                                }
                            }}
                            className={`px-3 py-1 rounded text-sm font-medium border ${filterMode === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'
                                }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => {
                                if (filterMode === 'inStockOnly') {
                                    loadProducts();
                                } else {
                                    setFilterMode('inStockOnly');
                                    setCurrentPage(1);
                                }
                            }}
                            className={`px-3 py-1 rounded text-sm font-medium border ${filterMode === 'inStockOnly' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'
                                }`}
                        >
                            Exists in Live Stock
                        </button>
                    </div>

                </div>


            </div>

            <div className="mt-56 md:mt-28 scroll-y-auto  px-1 md:px-8 w-full max-w-xl md:max-w-7xl  mx-auto">

                {loadingStocks && <div className="text-gray-500 text-center py-4">{t('loading')}</div>}
                {errorStocks && <div className="text-red-600 text-center py-4">{errorStocks}</div>}

                {loadingStocks && <div className="text-gray-500 text-center py-4">Loading stocks...</div>}
                {errorStocks && <div className="text-red-600 text-center py-4">{errorStocks}</div>}


                {totalPages > 1 && (
                    <div className="my-2 py-2 border-t"><Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></div>
                )}

                <div className="mt-3 md:mt-2">
                    {/* {stocks.map(stock => ( */}
                    {filteredStocks.map(stock => (

                        <div
                            key={stock.id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between bg-white border border-gray-200  hover:border-gray-400 border-b-indigo-400  hover:border-b-indigo-600 rounded shadow-sm  hover:shadow-xl p-1 pb-4 my-2 md:my-2"
                        >
                            <div className="flex flex-1 flex-col md:flex-row">
                                <div className="flex flex-row font-thin text-lg md:text-s">

                                    <span className='px-2'>
                                        <img
                                            src={
                                                Array.isArray(stock.media) && stock.media.length > 0
                                                    ? (stock.media[0]?.preview_url || stock.media[0]?.original_url || (window.APP_CONFIG?.PUBLIC_URL + "/placeholder.jpg"))
                                                    : (window.APP_CONFIG?.PUBLIC_URL ? window.APP_CONFIG.PUBLIC_URL + "/placeholder.jpg" : "/placeholder.jpg")
                                            }
                                            alt={stock.product_name}
                                            className="w-8 h-8 m-2"
                                        />
                                    </span>

                                    <span className="mr-2 text-gray-500 text-sm ">PID#{stock.id}</span>
                                    <span className="font-semibold tracking-wide
 text-green-700 text-sm md:text-sm">{stock.product_name}</span>
                                </div>
                                <div className="text-gray-500 text-sm mb-1 md:ml-4">
                                    <span>SKU: {stock.product_code}</span>
                                </div>
                            </div>
                            <div className="flex flex-row gap-6 mt-2 sm:mt-0">
                                {stock.stock_by_location !== null &&
                                    stock.stock_by_location !== undefined &&
                                    Object.keys(stock.stock_by_location).length > 0 && (
                                        <div>
                                            <div className="flex flex-row justify-end font-semibold">


                                                {/* Then show stock by location in REVERSED order */}
                                                {[...Object.entries(stock.stock_by_location)]
                                                    .reverse()
                                                    .filter(([_, qty]) => qty !== null && qty !== 0) // ⬅️ filter out null & 0
                                                    .map(([locationId, qty]) => (
                                                        <div key={locationId} className={` mr-2 w-30 border border-gray-300 rounded p-1 text-center ${Number(locationId) === 1 ? 'bg-indigo-100' : 'bg-green-' + `${Number(locationId)}` + '00'}`} >
                                                            <div className="text-xs text-gray-400">
                                                                Live Stock at {getLocationName(Number(locationId))}
                                                            </div>
                                                            {/* Loc {locationId}:  */}
                                                            {qty}

                                                            {qty}

                                                            {(() => {
                                                                const positions = stock.positions_by_location?.[locationId] ?? [];
                                                                return positions.map(pos => {
                                                                    const label = [
                                                                        pos.rack_code && `Rack ${pos.rack_code}`,
                                                                        pos.rack_level && `Lv ${pos.rack_level}`,
                                                                        pos.side && `Side ${pos.side}`,
                                                                        pos.box_code && `Box ${pos.box_code}`,
                                                                    ].filter(Boolean).join(' · ');

                                                                    if (!label) return null;

                                                                    return (
                                                                        <div key={pos.position_id} className="mt-1 pt-1 border-t border-gray-300 text-[11px] font-normal text-gray-600">
                                                                            {label}
                                                                            {positions.length > 1 && <span className="ml-1 text-gray-400">(×{pos.qty})</span>}
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}
                                                            
                                                        </div>
                                                    ))}

                                            
                                                {/* last show TOTAL */}
                                                {stock.total_quantity > 0 && (

                                                    <div className="w-20 text-sm text-gray-500 border border-blue-300 rounded p-1 text-center bg-orange-100">
                                                        <p>Total: </p><p className='text-xl'>{stock.total_quantity}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                            </div>

                        </div>
                    ))
                    }

                    {/* {stocks.length === 0 && ( */}
                    {filteredStocks.length === 0 && (
                        <div className="text-center text-gray-500 py-8">
                            No stock items found.
                        </div>
                    )}

                </div>


                {totalPages > 1 && (
                    <div className="my-2 py-2  border-t"><Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></div>
                )}

            </div>

        </div>
    );
};

export default StockList;